/**
 * ConfirmDestructive — reusable "type the confirm phrase to enable
 * the button" dialog for irreversible actions (Wipe, Send-All,
 * Delete, permanent Archive). Replaces `window.prompt` / `window.confirm`
 * on destructive routes so a mis-click can never blow away production
 * data or blast a mass email.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   const [running, setRunning] = useState(false);
 *
 *   <Button onClick={() => setOpen(true)}>Delete user</Button>
 *   <ConfirmDestructive
 *     open={open}
 *     onOpenChange={setOpen}
 *     tone="danger"                        // 'danger' | 'warn' | 'info'
 *     title="Delete user"
 *     description={<>This removes …</>}
 *     confirmPhrase={u.email}              // user must type this verbatim
 *     confirmHint="Type the user's email to enable Delete"
 *     confirmLabel="Delete permanently"
 *     running={running}
 *     testId="confirm-delete-user"
 *     onConfirm={async () => {
 *       setRunning(true);
 *       try { await api.delete(...); ... } finally { setRunning(false); }
 *     }}
 *   />
 *
 * Contract:
 * - Uses the shared shadcn <Dialog> so it sits above every other modal.
 * - Case-sensitive comparison — the phrase is meant to slow the user
 *   down. Pass a phrase that is easy to type when intentional but not
 *   accidentally producible (email address, "WIPE-SP-DATA", "SEND ALL").
 * - When `running` is true the input and Cancel are locked so the caller
 *   controls the entire lifecycle.
 * - Enter in the input triggers Confirm iff the phrase matches.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AlertTriangle, RefreshCw } from 'lucide-react';

const TONE = {
  danger: {
    bg: '#F5EAEA', border: '#8A3A3A', text: '#8A3A3A',
    btn: 'bg-[#8A3A3A] hover:bg-[#6E2F2F] text-white',
    phrase: 'text-[#8A3A3A]',
  },
  warn: {
    bg: '#FBF3E4', border: '#B58B36', text: '#7A5B1D',
    btn: 'bg-[#B58B36] hover:bg-[#8F6C29] text-white',
    phrase: 'text-[#7A5B1D]',
  },
  info: {
    bg: '#EFEAF3', border: '#4A3B5F', text: '#4A3B5F',
    btn: 'bg-[#4A3B5F] hover:bg-[#3B2E4C] text-white',
    phrase: 'text-[#4A3B5F]',
  },
};

export default function ConfirmDestructive({
  open,
  onOpenChange,
  tone = 'danger',
  title,
  description,
  confirmPhrase,
  confirmHint,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  running = false,
  testId = 'confirm-destructive',
  onConfirm,
  children,
}) {
  const [typed, setTyped] = useState('');
  const t = TONE[tone] || TONE.danger;
  const ok = typed === confirmPhrase && !running;

  // Reset the typed value every time the dialog opens so an old
  // confirmation from a previous action can't stick around and let the
  // next click succeed without re-typing.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const submit = () => {
    if (!ok) return;
    onConfirm?.();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (running) return; // never dismiss mid-run
        onOpenChange?.(o);
      }}
    >
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: t.text }}>
            <AlertTriangle size={18} /> {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="pt-2">{description}</DialogDescription>
          )}
        </DialogHeader>

        {children && <div className="pt-1">{children}</div>}

        <div
          className="rounded-md border p-3 text-xs"
          style={{ background: t.bg, borderColor: t.border, color: t.text }}
          data-testid={`${testId}-hint`}
        >
          {confirmHint || (
            <>
              Type <span className={`font-mono font-semibold ${t.phrase}`}>{confirmPhrase}</span> to enable the button.
            </>
          )}
        </div>

        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          disabled={running}
          placeholder={confirmPhrase}
          className="h-9 font-mono"
          data-testid={`${testId}-input`}
          autoFocus
        />

        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={running}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={submit}
            disabled={!ok}
            className={t.btn}
            data-testid={`${testId}-submit`}
          >
            {running ? <RefreshCw size={14} className="mr-2 animate-spin" /> : null}
            {running ? 'Running…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ConfirmDestructive };
